import { useCallback, useContext, useEffect, useRef } from 'react';
import { isRxDatabase } from 'rxdb';
import { useIsUserInitialized, useUser } from '@providers/Auth';
import teamsContext from '@providers/Teams/TeamsContext';
import { getNoodleDb, closeRxNoodleDB, handleRxDBPostLogoutUserProcess } from './rxdb.noodle';
import startReplications from './rxdb.replication';
import { useCreatorUI } from '../CreatorUI';
import { rxdbError } from './rxdb.tools';

const RxDBStorageProvider: React.FC = () => {
  const [user] = useUser();
  const { creatorId: creatorIdFromTeamsContext } = useContext(teamsContext);
  const creatorUI = useCreatorUI();
  const creatorId = creatorIdFromTeamsContext || creatorUI?.id;
  const lastCreatorIdUsed = useRef<string | null>(null);
  const lastUserIdUsed = useRef<string | null>(null);
  const isUserInitialized = useIsUserInitialized();
  const hasInitializedDB = useRef(false);
  const stopReplications = useRef<(() => void) | null>(null);

  const initializeDatabase = useCallback(async () => {
    try {
      if (!creatorId || !user?.id) {
        rxdbError('Cannot initialize database - missing creator ID or user ID');
        return;
      }

      stopReplications.current?.();
      await closeRxNoodleDB();

      hasInitializedDB.current = true;
      lastCreatorIdUsed.current = creatorId;
      lastUserIdUsed.current = user?.id;

      const db = await getNoodleDb({ creatorId, userId: user?.id });
      const isValidDB = isRxDatabase(db);

      if (!isValidDB) {
        rxdbError('Invalid database instance');
        return;
      }

      stopReplications.current = await startReplications({ creatorId });
    } catch (error) {
      rxdbError(`RXDB Provider - An error occurred while initializing the database: ${error}`);
    }
  }, [creatorId, user]);

  const resetDatabases = useCallback(() => {
    handleRxDBPostLogoutUserProcess();
    hasInitializedDB.current = false;
    lastCreatorIdUsed.current = null;
    lastUserIdUsed.current = null;
  }, []);

  useEffect(() => {
    if (isUserInitialized && lastCreatorIdUsed.current !== creatorId && user?.id && creatorId) {
      initializeDatabase();
    }
  }, [isUserInitialized, creatorId, lastCreatorIdUsed, initializeDatabase, user?.id]);

  useEffect(() => {
    // Reset the databases if the user logs out
    if (lastCreatorIdUsed.current && lastUserIdUsed.current && !creatorId && !user?.id) {
      resetDatabases();
    }
  }, [lastCreatorIdUsed, lastUserIdUsed, creatorId, user?.id, resetDatabases]);

  return null;
};

export default RxDBStorageProvider;
